import { FormEvent, useMemo, useState } from "react";
import { IdentityError, IdentityFormData, IdentityFormMode } from "./types";
import { IdentityButton } from "./IdentityButton";
import { PasswordStrengthMeter } from "./PasswordStrengthMeter";
import { IdentityA11yAnnouncer } from "./IdentityA11yAnnouncer";

export interface IdentityFormProps {
  mode: IdentityFormMode;
  onSubmit: (data: IdentityFormData) => void | Promise<void>;
  loading?: boolean;
  errors?: readonly IdentityError[];
  initialEmail?: string;
  submitLabel?: string;
}

export function IdentityForm({
  mode,
  onSubmit,
  loading,
  errors,
  initialEmail = "",
  submitLabel,
}: IdentityFormProps) {
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const isSignup = mode === "signup";
  const label =
    submitLabel ??
    (isSignup ? "Create SpeedyCore account" : "Continue with SpeedyCore");

  const errorsAnnouncement = useMemo(
    () => (errors && errors.length > 0 ? errors.map((e) => e.message).join(" ") : ""),
    [errors],
  );

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const data: IdentityFormData = isSignup
      ? { email: email.trim(), password, confirmPassword: confirm }
      : { email: email.trim(), password };
    await onSubmit(data);
  }

  return (
    <form className="sc-identity-form" onSubmit={(e) => void handleSubmit(e)} noValidate>
      <IdentityA11yAnnouncer message={errorsAnnouncement} type="error" />
      <label>
        <span className="sc-identity-field-label">Email</span>
        <input
          type="email"
          inputMode="email"
          name="email"
          id="identity-email"
          autoComplete="email"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          aria-required
          aria-describedby="identity-email-help"
          placeholder="you@example.com"
        />
        <span id="identity-email-help" className="sc-identity-field-hint" aria-hidden>
          We'll send login links and verification emails here.
        </span>
      </label>

      <label>
        <span className="sc-identity-field-label">Password</span>
        <span className="sc-identity-row">
          <input
            type={showPassword ? "text" : "password"}
            id="identity-password"
            name="password"
            autoComplete={isSignup ? "new-password" : "current-password"}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
            aria-describedby={isSignup ? "identity-password-strength" : undefined}
          />
          <button
            type="button"
            className="sc-identity-link-btn"
            aria-pressed={showPassword}
            aria-controls="identity-password"
            onClick={() => setShowPassword((shown) => !shown)}
          >
            {showPassword ? "Hide password" : "Show password"}
          </button>
        </span>
      </label>

      {isSignup ? (
        <PasswordStrengthMeter
          password={password}
          showLabel
          showBreachCheckMicrocopy
        />
      ) : null}

      {isSignup ? (
        <label>
          <span className="sc-identity-field-label">Confirm password</span>
          <input
            type="password"
            id="identity-confirm-password"
            name="confirm_password"
            autoComplete="new-password"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            minLength={8}
          />
        </label>
      ) : null}

      {errors && errors.length > 0
        ? errors.map((err, i) => (
            <p
              key={i}
              className="sc-identity-error"
              role="alert"
              data-code={err.code ?? ""}
            >
              {err.message}
            </p>
          ))
        : null}

      <IdentityButton type="submit" label={label} loading={loading} variant="primary" />
    </form>
  );
}

